import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import Highcharts from "highcharts";
import { WkoHighchart } from "../WkoHighchart";
import { WkoLoadingSpinner } from "../WkoLoadingSpinner";
import { notifyError } from "../../utils/notifications";
import { BenchmarkCard } from "./BenchmarkCard";
const LEVEL_COLORS = ["#FAF196", "#F7C245", "#FD6883", "#D87AE7"];
const LEVEL_NAMES = ["NCs Level 1", "NCs Level 2", "NCs Level 3", "NCs Level 4"];
export const HistoricalNcChart = ({ options, title = "Monthly trends", }) => {
    const [loading, setLoading] = useState(false);
    const [industrySeries, setIndustrySeries] = useState([]);
    const [levelsSeries, setLevelsSeries] = useState([]);
    const hasChartData = (chartSeries) => chartSeries.some((series) => "data" in series && Array.isArray(series.data) && series.data.length > 0);
    const fetchIndustryData = useCallback(() => {
        if (!options.isReady)
            return;
        setIndustrySeries([]);
        setLevelsSeries([]);
        setLoading(true);
        const params = new URLSearchParams({
            fromMonth: options.startDate.substring(0, 7),
            toMonth: options.endDate.substring(0, 7),
            vesselTypes: options.vesselTypes.join(","),
            fleets: options.fleet.map((f) => f.ID).join(","),
        });
        fetch(`/api/benchmark/ncdayspermonth?${params}`)
            .then((res) => {
            if (res.ok)
                return res.json();
            throw new Error(res.statusText);
        })
            .then((data) => {
            if (data.length === 0) {
                setIndustrySeries([]);
                return;
            }
            const fleetAverage = data.map((d) => [
                Date.parse(d.MonthAsDate),
                parseFloat((d.Fleet / d.FleetVesselCount).toFixed(2)),
            ]);
            const fleetTotal = data.map((d) => [Date.parse(d.MonthAsDate), d.Fleet]);
            const industryAvg = data.map((d) => {
                const parsed = parseFloat(String(d.Industry));
                return [Date.parse(d.MonthAsDate), isNaN(parsed) ? null : parseFloat(parsed.toFixed(2))];
            });
            setIndustrySeries([
                {
                    type: "spline",
                    data: fleetAverage,
                    name: "Fleet average NC days per vessel",
                    yAxis: 0,
                    color: "#5e90c3",
                },
                {
                    type: "column",
                    data: fleetTotal,
                    name: "(NC days total)",
                    color: "transparent",
                    borderColor: "transparent",
                    showInLegend: false,
                    visible: false,
                },
                {
                    type: "spline",
                    data: industryAvg,
                    name: "Industry average NC days per vessel",
                    yAxis: 0,
                    color: "#6c757d",
                },
            ]);
        })
            .catch(() => {
            notifyError("An error has occurred when loading historical NC data. Please try again.");
        })
            .finally(() => setLoading(false));
    }, [options.isReady, options.startDate, options.endDate, options.vesselTypes.join(","), options.fleet.map((f) => f.ID).join(",")]);
    const fetchLevelsData = useCallback(() => {
        if (!options.isReady)
            return;
        setIndustrySeries([]);
        setLevelsSeries([]);
        setLoading(true);
        const params = new URLSearchParams({
            fromMonth: options.startDate.substring(0, 7),
            toMonth: options.endDate.substring(0, 7),
            fleets: options.fleet.map((f) => f.ID).join(","),
            locationId: options.locationId !== undefined ? String(options.locationId) : "",
        });
        fetch(`/api/levelsbenchmark/ncdayspermonth?${params}`)
            .then((res) => {
            if (res.ok)
                return res.json();
            throw new Error(res.statusText);
        })
            .then((data) => {
            const levels = [[], [], [], []];
            const grouped = {};
            for (const item of data) {
                if (!grouped[item.Month])
                    grouped[item.Month] = [];
                grouped[item.Month].push(item);
            }
            for (const month of Object.keys(grouped)) {
                for (const item of grouped[month]) {
                    const result = [
                        ((dateString) => Date.parse(`${dateString.slice(0, 4)}-${dateString.slice(4, 6)}`))(item.Month),
                        parseFloat((item.NcDays / item.VesselCount).toFixed(3)),
                        item.NcDays,
                        item.VesselCount,
                    ];
                    if (item.NcLevel >= 1 && item.NcLevel <= 4) {
                        levels[item.NcLevel - 1].push(result);
                    }
                }
            }
            const nextSeries = levels.map((data, i) => ({
                type: "column",
                keys: ["x", "y", "ncdays", "vessels"],
                data,
                name: LEVEL_NAMES[i],
                color: LEVEL_COLORS[i],
            }));
            setLevelsSeries(hasChartData(nextSeries) ? nextSeries : []);
        })
            .catch(() => {
            notifyError("An error has occurred when loading historical NC levels data. Please try again.");
        })
            .finally(() => setLoading(false));
    }, [options.isReady, options.startDate, options.endDate, options.fleet, options.locationId]);
    useEffect(() => {
        if (options.isIndustryMode) {
            fetchIndustryData();
        }
        else {
            fetchLevelsData();
        }
    }, [options.isIndustryMode, fetchIndustryData, fetchLevelsData]);
    const industryChartOptions = useMemo(() => ({
        chart: { styledMode: false },
        credits: { enabled: false },
        title: { text: undefined },
        exporting: { enabled: false },
        legend: {
            symbolHeight: 20,
            symbolRadius: 0,
            symbolWidth: 20,
        },
        plotOptions: {
            series: {
                dataLabels: { enabled: false },
                marker: { enabled: false },
                tooltip: { dateTimeLabelFormats: { month: "%b-%y" } },
            },
        },
        xAxis: {
            type: "datetime",
            dateTimeLabelFormats: { month: "%b-%y" },
        },
        yAxis: {
            title: { text: "Average per vessel" },
        },
        tooltip: {
            xDateFormat: "%b-%y",
            shared: true,
            formatter: function () {
                let s = Highcharts.dateFormat("%b-%y", this.x) + "<br/>";
                const chart = this.points?.[0]?.series.chart;
                if (chart) {
                    for (const series of chart.series) {
                        const seriesData = series.options.data;
                        const point = seriesData?.find((d) => Array.isArray(d) && d[0] === this.x);
                        if (point) {
                            s += `<span style="color:${series.color}">●</span> ${series.name}: <b>${point[1]}</b><br/>`;
                        }
                    }
                }
                return s;
            },
        },
        series: industrySeries,
    }), [industrySeries]);
    const levelsChartOptions = useMemo(() => ({
        chart: { type: "column", styledMode: false },
        credits: { enabled: false },
        title: { text: undefined },
        exporting: { enabled: false },
        legend: {
            borderWidth: 0,
            itemStyle: { fontSize: "8pt" },
        },
        plotOptions: {
            series: {
                dataLabels: { enabled: false },
                marker: { enabled: false },
                tooltip: { dateTimeLabelFormats: { month: "%b-%y" } },
                stacking: "normal",
            },
            column: {
                stacking: "normal",
                dataLabels: { enabled: false },
            },
        },
        xAxis: {
            type: "datetime",
            dateTimeLabelFormats: { month: "%b-%y" },
        },
        yAxis: {
            title: { text: "Average per vessel" },
            stackLabels: { enabled: false },
            reversedStacks: false,
        },
        tooltip: {
            pointFormat: "{series.name}: {point.y} per vessel",
        },
        series: levelsSeries,
    }), [levelsSeries]);
    if (loading) {
        return (_jsx(BenchmarkCard, { title: title, children: _jsx(WkoLoadingSpinner, {}) }));
    }
    const chartOptions = options.isIndustryMode ? industryChartOptions : levelsChartOptions;
    const hasSeries = options.isIndustryMode ? hasChartData(industrySeries) : hasChartData(levelsSeries);
    if (!hasSeries) {
        return (_jsx(BenchmarkCard, { title: title, children: _jsx("div", { className: "py-5", children: _jsx("p", { className: "text-secondary text-center mb-0", children: "No data for this selection." }) }) }));
    }
    return (_jsx(BenchmarkCard, { title: title, children: _jsx(WkoHighchart, { options: chartOptions }) }));
};
